import { useState, useEffect, useCallback, useMemo } from 'react';
import { getItems } from './fitnessApi';
import {
  totalVolumeLb,
  parseTemplateSets,
  hardSetsByMuscle,
  HARD_SETS_TARGET_PER_MUSCLE_PER_WEEK,
} from './liftingBurn';

const WORKOUTS_LIST = 'LiftingWorkouts';
const TEMPLATES_LIST = 'LiftingTemplates';

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

// Display order and labels for the muscle vocabulary. A key that shows up
// in the data but not here still renders — capitalized raw — rather than
// being silently dropped.
const MUSCLE_LABELS = [
  ['chest', 'Chest'], ['delts', 'Delts'], ['triceps', 'Triceps'],
  ['lats', 'Lats'], ['upperback', 'Upper Back'], ['biceps', 'Biceps'],
  ['quads', 'Quads'], ['glutes', 'Glutes'], ['hamstrings', 'Hams'],
  ['lowerback', 'Lower Back'], ['core', 'Core'], ['traps', 'Traps'],
  ['grip', 'Grip'],
];

// SVG layout constants — same grid as the swim dashboard's VolumeChart.
const SVG_W = 600;
const PAD_L = 44;
const PAD_R = 12;
const PAD_T = 18;
const CW = SVG_W - PAD_L - PAD_R;
const V_SVG_H = 130;
const V_PAD_B = 34;
const VCH = V_SVG_H - PAD_T - V_PAD_B;

function labelIndices(n, maxLabels = 6) {
  if (n <= maxLabels) return Array.from({ length: n }, (_, i) => i);
  const step = Math.ceil(n / maxLabels);
  const idx = Array.from({ length: n }, (_, i) => i).filter(i => i % step === 0);
  if (idx[idx.length - 1] !== n - 1) idx.push(n - 1);
  return idx;
}

// The lifting dashboard. TotalVolumeLb is to this screen what
// TotalDistanceMeters is to the swim one: the hero, the totals, and the
// per-session chart are all the same number at three zoom levels — it is
// the progression signal.
function LiftingDashboard({ getToken, userEmail }) {
  const [workouts, setWorkouts] = useState([]);
  // Every muscle the current templates program, alts included — so the
  // weekly card shows 0/6 for a muscle that was PLANNED and skipped, while
  // a muscle the program never targets stays off the card entirely.
  const [programmedMuscles, setProgrammedMuscles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const [raw, tmpl] = await Promise.all([
        getItems(token, WORKOUTS_LIST),
        getItems(token, TEMPLATES_LIST),
      ]);
      const fromTemplates = new Set();
      for (const t of tmpl) {
        for (const entry of parseTemplateSets(t.Sets)) {
          for (const g of entry.muscleGroups || []) fromTemplates.add(g);
          for (const g of entry.alt?.muscleGroups || []) fromTemplates.add(g);
        }
      }
      setProgrammedMuscles([...fromTemplates]);
      const mine = raw
        .filter(w => w.CreatedByEmail === userEmail)
        .map(w => ({
          id: w.id,
          sets: safeParse(w.Sets),
          templateName: w.TemplateName || w.Title || '',
          // Stored value is authoritative; recompute only for rows without one.
          totalVolumeLb: w.TotalVolumeLb || totalVolumeLb(safeParse(w.Sets)),
          date: w.WorkoutDate || w.Created || '',
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setWorkouts(mine);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Best weight per exercise across all sessions — the lifting analog of the
  // swim PR list, read straight off the saved set entries.
  const topWeights = useMemo(() => {
    const best = {};
    workouts.forEach(w => {
      w.sets.forEach(s => {
        const name = (s.exercise || '').trim();
        const lb = Number(s.weightLb);
        if (!name || !Number.isFinite(lb) || lb <= 0) return;
        if (!best[name] || lb > best[name].weightLb) {
          best[name] = { exercise: name, weightLb: lb, reps: s.actualReps ?? s.reps, date: w.date };
        }
      });
    });
    return Object.values(best).sort((a, b) => b.weightLb - a.weightLb);
  }, [workouts]);

  // Trailing 7 days of hard sets per muscle — the retention metric this
  // dashboard exists for. Null-RIR sets count as hard (see hardSetsByMuscle
  // for why) and the ⁺ says when that assumption is in the number.
  const weekSets = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const windowWorkouts = workouts.filter(w => {
      const t = new Date(w.date).getTime();
      return Number.isFinite(t) && t >= cutoff;
    });
    return {
      sessionCount: windowWorkouts.length,
      byMuscle: hardSetsByMuscle(windowWorkouts.map(w => w.sets)),
    };
  }, [workouts]);

  const totalVolume = workouts.reduce((s, w) => s + w.totalVolumeLb, 0);
  const totalSessions = workouts.length;
  const weekCutoff = new Date();
  weekCutoff.setDate(weekCutoff.getDate() - 7);
  const thisWeekVolume = workouts
    .filter(w => new Date(w.date) >= weekCutoff)
    .reduce((s, w) => s + w.totalVolumeLb, 0);

  const latest = workouts[workouts.length - 1] ?? null;
  const previous = workouts[workouts.length - 2] ?? null;
  const volumeDelta = latest && previous ? latest.totalVolumeLb - previous.totalVolumeLb : null;

  if (loading) return <p className="Fitness-loading">Loading dashboard...</p>;

  if (totalSessions === 0) {
    return (
      <div className="SwimDash">
        <p className="Fitness-empty">Log your first lift to see your dashboard.</p>
      </div>
    );
  }

  return (
    <div className="SwimDash">
      {error && <p className="Fitness-error">{error}</p>}

      {/* HERO — most recent session's volume, the progression signal */}
      <div className="SwimDash-hero">
        <div className="SwimDash-hero-pace">
          {Math.round(latest.totalVolumeLb).toLocaleString('en-US')} lb
        </div>
        <div className="SwimDash-hero-label">
          total volume · most recent session
        </div>
        {volumeDelta !== null && (
          <div className={`SwimDash-hero-delta ${volumeDelta >= 0 ? 'SwimDash-hero-delta--faster' : 'SwimDash-hero-delta--slower'}`}>
            {volumeDelta >= 0
              ? `+${Math.round(volumeDelta).toLocaleString('en-US')} lb over last`
              : `−${Math.round(Math.abs(volumeDelta)).toLocaleString('en-US')} lb under last`}
          </div>
        )}
      </div>

      {/* SETS THIS WEEK — hard sets per muscle, trailing 7 days. The
          lifting equivalent of the protein run rate card: the number that
          predicts retention, against the hardcoded 6/muscle/week target.
          Zero sessions in the window renders the em dash, never a grid of
          zeros — an unlogged week is not a zero-work week. */}
      <div className="SwimDash-section">
        <h3 className="SwimDash-section-title">
          Sets This Week
          <span className="SwimDash-section-sub">
            {' '}· hard sets per muscle · target {HARD_SETS_TARGET_PER_MUSCLE_PER_WEEK}
          </span>
        </h3>
        {weekSets.sessionCount === 0 ? (
          <p className="Fitness-empty LiftDash-muscle-empty">&mdash; no sessions in the last 7 days</p>
        ) : (
          <div className="LiftDash-muscle-grid">
            {(() => {
              const known = MUSCLE_LABELS.filter(([key]) =>
                programmedMuscles.includes(key) || weekSets.byMuscle[key]
              );
              const extras = Object.keys(weekSets.byMuscle)
                .filter(key => !MUSCLE_LABELS.some(([k]) => k === key))
                .map(key => [key, key.charAt(0).toUpperCase() + key.slice(1)]);
              return [...known, ...extras].map(([key, label]) => {
                const counts = weekSets.byMuscle[key] || { hard: 0, unrated: 0 };
                const met = counts.hard >= HARD_SETS_TARGET_PER_MUSCLE_PER_WEEK;
                return (
                  <div key={key} className="LiftDash-muscle-row">
                    <span className="LiftDash-muscle-name">{label}</span>
                    <span className={`LiftDash-muscle-count${met ? ' LiftDash-muscle-count--met' : ''}`}>
                      {counts.hard}
                      {counts.unrated > 0 && (
                        <sup
                          className="LiftDash-muscle-unrated"
                          title={`Includes ${counts.unrated} unrated ${counts.unrated === 1 ? 'set' : 'sets'} — counted as hard`}
                        >
                          +
                        </sup>
                      )}
                      {' / '}{HARD_SETS_TARGET_PER_MUSCLE_PER_WEEK}
                      {met ? ' ✓' : ''}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {/* TOTALS */}
      <div className="SwimDash-totals">
        <div className="SwimDash-metric">
          <span className="SwimDash-metric-label">All-Time Volume</span>
          <strong className="SwimDash-metric-value">
            {Math.round(totalVolume).toLocaleString('en-US')} lb
          </strong>
          <span className="SwimDash-metric-sub">{(totalVolume / 2000).toFixed(1)} tons</span>
        </div>
        <div className="SwimDash-metric">
          <span className="SwimDash-metric-label">Total Sessions</span>
          <strong className="SwimDash-metric-value">{totalSessions}</strong>
        </div>
        <div className="SwimDash-metric">
          <span className="SwimDash-metric-label">This Week</span>
          <strong className="SwimDash-metric-value">
            {Math.round(thisWeekVolume).toLocaleString('en-US')} lb
          </strong>
        </div>
      </div>

      {/* VOLUME PER SESSION */}
      <div className="SwimDash-section">
        <h3 className="SwimDash-section-title">Volume per Session</h3>
        <LiftVolumeChart sessions={workouts} />
      </div>

      {/* TOP WEIGHTS */}
      {topWeights.length > 0 && (
        <div className="SwimDash-section">
          <h3 className="SwimDash-section-title">
            Top Weights
            <span className="SwimDash-section-sub"> · heaviest recorded per exercise</span>
          </h3>
          <div className="SwimHistory-stats">
            {topWeights.map(t => (
              <div key={t.exercise} className="SwimHistory-stat-row">
                <span>{t.exercise}</span>
                <strong>{t.weightLb} lb × {t.reps ?? '—'}</strong>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Bars in equal slots — the swim VolumeChart's geometry with pounds on the
// Y axis. Amber, the lifting accent, against swim's purple.
function LiftVolumeChart({ sessions }) {
  const [tip, setTip] = useState({ visible: false, x: 0, y: 0, text: '' });
  const showTip = (e, text) => setTip({ visible: true, x: e.clientX, y: e.clientY, text });
  const moveTip = e => setTip(t => ({ ...t, x: e.clientX, y: e.clientY }));
  const hideTip = () => setTip(t => ({ ...t, visible: false }));
  const n = sessions.length;
  const maxLb = Math.max(...sessions.map(s => s.totalVolumeLb), 1);
  const slotW = CW / n;
  const barW = Math.max(slotW - 2, 1);

  const xLabels = labelIndices(n);

  return (
    <div className="SwimDash-chart-wrap">
      <svg width="100%" viewBox={`0 0 ${SVG_W} ${V_SVG_H}`} aria-label="Volume per session">
        <text x={PAD_L - 5} y={PAD_T + 10} textAnchor="end" fontSize="11" fill="#bbb">
          {Math.round(maxLb).toLocaleString()} lb
        </text>
        <text x={PAD_L - 5} y={PAD_T + VCH + 1} textAnchor="end" fontSize="11" fill="#bbb">
          0
        </text>
        <line x1={PAD_L} y1={PAD_T + VCH} x2={PAD_L + CW} y2={PAD_T + VCH} stroke="#eee" strokeWidth="1" />
        {sessions.map((s, i) => {
          const bh = Math.max((s.totalVolumeLb / maxLb) * VCH, 2);
          const dateStr = new Date(s.date).toLocaleDateString();
          const volStr = `${Math.round(s.totalVolumeLb).toLocaleString()} lb`;
          const line1Parts = [dateStr, volStr];
          if (s.templateName) line1Parts.push(s.templateName);
          const setList = s.sets.map(set => {
            const name = (set.exercise || '').trim();
            if (!name) return null;
            const lb = Number(set.weightLb);
            const detail = `${set.actualSets ?? set.sets}x${set.actualReps ?? set.reps}`;
            return Number.isFinite(lb) && lb > 0 ? `${name} ${detail} @ ${lb}` : `${name} ${detail}`;
          }).filter(Boolean).join(' · ');
          const tipText = setList ? `${line1Parts.join(' · ')}\n${setList}` : line1Parts.join(' · ');
          return (
            <g key={s.id}>
              <rect x={PAD_L + i * slotW + (slotW - barW) / 2} y={PAD_T + VCH - bh} width={Math.max(barW, 1)} height={bh} fill="#B8860B" rx={1} />
              <rect
                x={PAD_L + i * slotW} y={PAD_T} width={slotW} height={VCH}
                fill="#000" fillOpacity="0" pointerEvents="all"
                onMouseEnter={e => showTip(e, tipText)}
                onMouseMove={moveTip}
                onMouseLeave={hideTip}
              />
            </g>
          );
        })}
        {xLabels.map(i => (
          <text key={i} x={PAD_L + i * slotW + slotW / 2} y={V_SVG_H - 5} textAnchor="middle" fontSize="10" fill="#999">
            {new Date(sessions[i].date).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
          </text>
        ))}
      </svg>
      {tip.visible && (
        <div className="SwimDash-tooltip" style={{ left: tip.x + 12, top: tip.y < 60 ? tip.y + 16 : tip.y - 44 }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}

export default LiftingDashboard;
